import { FunctionComponent, useEffect, useRef, useState } from "react";
import styles from "../AddContacts/AddContacts.module.css";
import { connect, useDispatch } from "react-redux";
import { themeColors } from "../../Components/theme";
import { showSnackbarAction } from "../../Redux/actions";
import "../../theme-vars.css";
import { Circles } from "react-loader-spinner";
import { useTheme, useMediaQuery, TextField, Grid } from "@mui/material";
import StyledSubmitButton from "../../Components/Buttons/StyledSubmitButton";
import {
  getWhatsAppConfigById,
  updateWhatsAppConfig,
} from "../../Services/organizations";
import InputTextValue from "../../Components/TextInput/InputTextValue";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router-dom";

type props = {
  theme: any;
  organizationId: any;
};

const primaryColor = "#292744";

// 🔹 Custom styled TextField
const CustomTextArea = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    fontSize: "var(--font-size-medium)",
    fontFamily: "var(--font-family-main)",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px",
    fontSize: 14,
    color: "#222",
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    fontSize: 14,
    color: primaryColor,
  },
});

// 🔹 Default Lead JSON Templates
const defaultLeadIncomingJson = JSON.stringify(
  {
    "Customer Name": "customer_name",
    "Contact Number": "contact_no",
    "Alternate Number": "alternate_no",
    "Country Code": "country_code",
    "Lead Type": "lead_type",
    "Email": "email",
    "Lead Source": "lead_source",
    "Organization Name": "organization_name",
    "Message": "Hii"
  },
  null,
  2
);

const defaultLeadTransferJson = JSON.stringify(
  {
    "Customer Name": "customer_name",
    "Contact Number": "contact_no",
    "Alternate Number": "alternate_no",
    "Country Code": "country_code",
    "Lead Type": "lead_type",
    "Email": "email",
    "Lead Source": "lead_source",
    "Organization Name": "organization_name",
    "Message": "Hii"
  },
  null,
  2
);

const WAPIWhatsAppIntegration: FunctionComponent<props> = ({ theme, organizationId }) => {
  const dispatcher = useDispatch();
  const history = useHistory();
  const [load, setLoad] = useState(false);
  const [list, setList] = useState<any[]>([]);
  // 🔹 WAPI Fields
  const [wapiUrl, setWapiUrl] = useState("");
  const [wapiToken, setWapiToken] = useState("");
  const [wapiId, setWapiId] = useState("");
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const [wapiActive, setWapiActive] = useState(false);
  const [incomingMessage, setIncomingMessage] = useState("");
  const [transferMessage, setTransferMessage] = useState("");
  const textAreaIncomingRef = useRef<any>(null);
  const textAreaTransferRef = useRef<any>(null);

  // ✅ load data from Firestore
  useEffect(() => {
    if (!organizationId || organizationId.trim() === "") {
      console.warn("organizationId is empty → skipping Firestore call");
      return;
    }
    const unsub = getWhatsAppConfigById(organizationId, setList);
    return () => unsub();
  }, [organizationId]);


  // ✅ Set fields from Firestore
  useEffect(() => {
    if (!organizationId || !list || list.length === 0) return;
    if (list.length > 0) {
      const c = list[0];

      // ⭐ CREATE UNIQUE ID IF NOT EXISTS
      if (!c.wapi?.id) {
        const newId = crypto.randomUUID();
        updateWhatsAppConfig(organizationId, { wapi: { ...c.wapi, id: newId, type: "WAPI" } });
        setWapiId(newId);
      } else {
        setWapiId(c.wapi.id);
      }
      if (c.wapi) {
        setWapiUrl(c.wapi.wapi_url || "");
        setWapiToken(c.wapi.wapi_token || "");
        setWapiActive(c.wapi.active || false);
        setIncomingMessage(c.wapi.incoming_json || "");
        setTransferMessage(c.wapi.transfer_json || "");
      }
    }
  }, [list]);

  // ✅ Update Firestore
  const create = async (e: any) => {
    e.preventDefault();
    if (!wapiUrl || !wapiToken)
      return dispatcher(showSnackbarAction("WAPI: Missing fields", "error"));

    setLoad(true);

    const payload = {
      wapi: {
        id: wapiId,
        wapi_url: wapiUrl,
        wapi_token: wapiToken,
        type: "WAPI",
        active: wapiActive,
        incoming_json: incomingMessage,
        transfer_json: transferMessage,
      },
    };

    try {
      await updateWhatsAppConfig(organizationId, payload);
      dispatcher(showSnackbarAction("Updated successfully!", "success"));
      history.push("/whatsAppIntegration");
    } catch (err) {
      dispatcher(showSnackbarAction("Error updating config", "error"));
      console.log(err);
    }
    setLoad(false);
  };

  // ✅ Copy Handlers
  const handleCopyIncoming = () => {
    if (textAreaIncomingRef.current) {
      const textToCopy = textAreaIncomingRef.current.value;
      navigator.clipboard.writeText(textToCopy);
      dispatcher(showSnackbarAction("Incoming JSON copied!", "success"));
    }
  };

  const handleCopyTransfer = () => {
    if (textAreaTransferRef.current) {
      const textToCopy = textAreaTransferRef.current.value;
      navigator.clipboard.writeText(textToCopy);
      dispatcher(showSnackbarAction("Transfer JSON copied!", "success"));
    }
  };

  return (
    <div
      className={styles.parent}
      style={{
        backgroundColor: theme
          ? themeColors.backgroundColor
          : themeColors.backgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
        marginTop: isMobile ? "6%" : "4%",
        fontFamily: "var(--font-family-main)",
        height: "100vh", // full screen height
        overflow: "hidden", // prevent double scroll
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        className="w-100 d-flex flex-column gap-2 px-4"
        style={{
          maxWidth: isMobile ? "100vw" : undefined,
          margin: "0 auto",
          flex: 1,
          overflowY: "auto",
          scrollBehavior: "smooth",
          paddingBottom: "80px",
          height: "calc(100vh - 120px)", // adjust based on header/footer height
        }}
      >
        <form
          className="card p-4 shadow rounded mt-2"
          style={{
            backgroundColor: theme
              ? themeColors.cardBackgroundColor
              : themeColors.cardBackgroundColorDark,
            overflowY: "auto",
            maxHeight: "calc(100vh - 80px)", // scroll inside form if needed
            scrollbarWidth: "thin",
            scrollbarColor: "#ccc transparent",
          }}
        >
          <Grid container spacing={2}>
            {/* ✅ WAPI SECTION */}
            <Grid item xs={12} sm={6}>
              <InputTextValue
                title="WAPI URL"
                required
                value={wapiUrl}
                onChange={(e: any) => setWapiUrl(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputTextValue
                title="WAPI Token"
                required
                value={wapiToken}
                onChange={(e: any) => setWapiToken(e.target.value)}
              />
            </Grid>
            {/* 🟢 Lead Incoming Section */}
            <Grid item xs={12} sm={6}>
              <div style={{ position: "relative" }}>
                <CustomTextArea
                  inputRef={textAreaIncomingRef}
                  fullWidth
                  label="Lead (Incoming)"
                  variant="outlined"
                  multiline
                  rows={10}
                  onChange={(e: any) => setIncomingMessage(e.target.value)}
                  value={incomingMessage?.trim() ? incomingMessage : defaultLeadIncomingJson}
                />
                <button
                  type="button"
                  onClick={handleCopyIncoming}
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 12,
                    backgroundColor: primaryColor,
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    cursor: "pointer",
                    fontFamily: "var(--font-family-main)",
                    fontSize: 13,
                  }}
                >
                  Copy
                </button>
              </div>
            </Grid>
            {/* 🟣 Lead Transfer Section */}
            <Grid item xs={12} sm={6}>
              <div style={{ position: "relative" }}>
                <CustomTextArea
                  inputRef={textAreaTransferRef}
                  fullWidth
                  label="Lead (Transfer)"
                  variant="outlined"
                  multiline
                  rows={10}
                  onChange={(e: any) => setTransferMessage(e.target.value)}
                  value={transferMessage?.trim() ? transferMessage : defaultLeadTransferJson}
                />
                <button
                  type="button"
                  onClick={handleCopyTransfer}
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 12,
                    backgroundColor: primaryColor,
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    cursor: "pointer",
                    fontFamily: "var(--font-family-main)",
                    fontSize: 13,
                  }}
                >
                  Copy
                </button>
              </div>
            </Grid>
            {/* ✅ Submit */}
            <Grid item xs={12} style={{ marginTop: 20 }}>
              <StyledSubmitButton
                onClick={create}
                themeMode={themeMUI.palette.mode}
                isMobile={isMobile}
              >
                {load ? (
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <Circles color="#fff" height={20} width={20} />
                    &nbsp;Submit...
                  </span>
                ) : (
                  <>Submit</>
                )}
              </StyledSubmitButton>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  theme: state.theme.isLightMode,
  organizationId: state.organization.id,
});

export default connect(mapStateToProps)(WAPIWhatsAppIntegration);
